import React from "react";
import { Typography, Box, LinearProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";

const BLUE = "#3B82F6";
const BLUE_DIM = "rgba(59,130,246,0.08)";
const BLUE_BORDER = "rgba(59,130,246,0.2)";
const GREEN = "#22c27a";
const GREEN_DIM = "rgba(34,194,122,0.08)";
const GREEN_BORDER = "rgba(34,194,122,0.25)";
const BORDER = "#dde0ee";
const SURFACE = "#f5f6fa";
const TEXT = "#1a1d2e";
const MUTED = "#7b80a0";

export const STEPS = [
  "Preparing document for analysis",
  "Reading and interpreting content",
  "Identifying important concepts",
  "Discovering patterns and relationships",
  "Extracting key information",
  "Building contextual understanding",
  "Generating structured summaries",
  "Reviewing analysis results",
  "Refining insights",
  "Completing document analysis",
];

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    padding: "4px 0",
  },

  // Top linear progress bar
  loaderContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    height: 4,
  },
  linearProgress: {
    height: 3,
    backgroundColor: "transparent",
    "& .MuiLinearProgress-barColorPrimary": {
      backgroundColor: BLUE,
    },
  },
  loaderGlow: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 20,
    background: "linear-gradient(to bottom, rgba(0,98,255,0.1), transparent)",
    pointerEvents: "none",
  },

  // Uploading card
  uploadingCard: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    background: BLUE_DIM,
    border: `0.5px solid ${BLUE_BORDER}`,
    borderRadius: 7,
    padding: "12px 13px",
  },
  uploadingHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  uploadIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 6,
    background: BLUE_DIM,
    border: `0.5px solid ${BLUE_BORDER}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": { color: BLUE, fontSize: 17 },
  },
  uploadingMeta: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  uploadingFileName: { fontSize: 12, fontWeight: 500, color: TEXT, lineHeight: 1.4 },
  uploadingStatus: { fontSize: 11, color: MUTED },
  uploadProgressTrack: {
    height: 3,
    borderRadius: 3,
    backgroundColor: BORDER,
    "& .MuiLinearProgress-barColorPrimary": {
      backgroundColor: BLUE,
      borderRadius: 3,
    },
  },

  // File uploaded banner
  fileBanner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: GREEN_DIM,
    border: `0.5px solid ${GREEN_BORDER}`,
    borderRadius: 7,
    padding: "10px 13px",
  },
  fileIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 6,
    background: "rgba(34,194,122,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    "& svg": { color: GREEN, fontSize: 17 },
  },
  fileName: { fontSize: 12, fontWeight: 500, color: TEXT, lineHeight: 1.4 },
  fileStatus: { fontSize: 11, color: GREEN, marginTop: 1 },
  checkIcon: { color: GREEN, fontSize: 18, marginLeft: "auto", flexShrink: 0 },

  // Analyzing card
  analyzingCard: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    background: BLUE_DIM,
    border: `0.5px solid ${BLUE_BORDER}`,
    borderRadius: 7,
    padding: "12px 13px",
  },
  analyzingHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  spinDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: BLUE,
    flexShrink: 0,
    animation: "$pulse 1.2s ease-in-out infinite",
  },
  analyzingLabel: { fontSize: 12, fontWeight: 500, color: TEXT },
  currentStep: { fontSize: 11, color: MUTED, marginLeft: 16 },
  progressTrack: {
    height: 3,
    borderRadius: 3,
    backgroundColor: BORDER,
    "& .MuiLinearProgress-barColorPrimary": {
      backgroundColor: BLUE,
      borderRadius: 3,
    },
  },

  "@keyframes pulse": {
    "0%, 100%": { opacity: 1, transform: "scale(1)" },
    "50%": { opacity: 0.4, transform: "scale(0.75)" },
  },
}));

export default function AnalyzingStage({ uploading, analyzing, stepStates, fileName }) {
  const cls = useStyles();

  const doneCount = stepStates ? stepStates.filter((s) => s === "done").length : 0;
  const activeIndex = stepStates ? stepStates.lastIndexOf("active") : -1;
  const currentStep = activeIndex !== -1 ? STEPS[activeIndex] : STEPS[0];
  const progress = Math.round((doneCount / STEPS.length) * 100);

  return (
    <div className={cls.root}>
      {/* Stage 1 — uploading */}
      {uploading && (
        <div className={cls.uploadingCard}>
          <div className={cls.uploadingHeader}>
            <div className={cls.uploadIconWrap}>
              <CloudUploadOutlinedIcon />
            </div>
            <div className={cls.uploadingMeta}>
              <Typography className={cls.uploadingFileName}>
                {fileName || "document.pdf"}
              </Typography>
              <Typography className={cls.uploadingStatus}>Uploading file...</Typography>
            </div>
          </div>
          <LinearProgress className={cls.uploadProgressTrack} />
        </div>
      )}

      {/* Stage 2 — file uploaded + analyzing */}
      {analyzing && (
        <>
          <div className={cls.fileBanner}>
            <div className={cls.fileIconWrap}>
              <InsertDriveFileOutlinedIcon />
            </div>
            <div>
              <Typography className={cls.fileName}>
                {fileName || "document.pdf"}
              </Typography>
              <Typography className={cls.fileStatus}>Uploaded successfully</Typography>
            </div>
            <CheckCircleOutlineIcon className={cls.checkIcon} />
          </div>

          <div className={cls.analyzingCard}>
            <div className={cls.analyzingHeader}>
              <div className={cls.spinDot} />
              <Typography className={cls.analyzingLabel}>AI is analyzing your document</Typography>
            </div>
            <Typography className={cls.currentStep}>{currentStep}</Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              classes={{ root: cls.progressTrack }}
            />
          </div>
        </>
      )}

    </div>
  );
}